import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import Zone from "@insite/client-framework/Components/Zone";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSession } from "@insite/client-framework/Store/Context/ContextSelectors";
import dismissRfqUpdates from "@insite/client-framework/Store/Context/Handlers/DismissRfqUpdates";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import PageModule from "@insite/client-framework/Types/PageModule";
import PageProps from "@insite/client-framework/Types/PageProps";
import { SkipNavStyles } from "@insite/content-library/Components/SkipNav";
import baseTheme from "@insite/mobius/globals/baseTheme";
import Hidden from "@insite/mobius/Hidden";
import Link from "@insite/mobius/Link";
import Page, { PageProps as MobiusPageProps } from "@insite/mobius/Page";
import { HasToasterContext, withToaster } from "@insite/mobius/Toast/ToasterContext";
import Typography from "@insite/mobius/Typography";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import getColor from "@insite/mobius/utilities/getColor";
import { HasHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { useEffect, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const mapStateToProps = (state: ApplicationState) => ({
    siteName: state.context.website.name,
    session: getSession(state),
    myAccountPageUrl: getPageLinkByPageType(state, "MyAccountPage")?.url,
});

const mapDispatchToProps = {
    dismissRfqUpdates,
};

type Props = PageProps &
    ReturnType<typeof mapStateToProps> &
    ResolveThunks<typeof mapDispatchToProps> &
    HasToasterContext &
    HasHistory;

export interface HeaderStyles {
    page?: MobiusPageProps;
    topHeader?: InjectableCss;
    logoWrapper?: InjectableCss;
    container?: InjectableCss;
    skipToContent?: SkipNavStyles;
    websiteName?: InjectableCss;
    navigation?: InjectableCss;
    signIn?: InjectableCss;
    searchWrapper?: InjectableCss;
    slogan?: InjectableCss;
}

export const headerStyles: HeaderStyles = {
    page: {
        fullWidth: [true, true, true, true, true], // make the header full width and control the margins with container css
        padding: 0,
        css: css`
            background-color: #FFFFFF;            
            height: 104px;
            background-color: ${getColor("common.background")};
            color: ${getColor("primary")};
            display: inline-block;
            margin-bottom: 20px;
            box-shadow: 0px 5.5px 35px -3px rgba(0, 55, 68, 0.1);
            -webkit-transition: 0.3s;
            transition: 0.3s;  
            position: fixed;
            z-index: 10;          

            &.hide-header {
                -webkit-transform: translateY(-100%);
                transform: translateY(-100%)               
            }
        `,
    },
    topHeader: {
        css: css`
            width: 100%;
            background-color: ${getColor("common.background")};
        `,
    },
    logoWrapper: {
        css: css`
            padding: 15px;           
            display: flex;
            align-items: center;
            margin-right: 27px;

            > div {
                height: 50px;
                align-items: center;
                display: flex;
            }

            img {
                ${({ theme }) => {
                return breakpointMediaQueries(theme, [
                    css`
                            width: 115.6545px !important;
                            height: 24.947px !important;
                        `,
                    css`
                            width: 133.4475px !important;
                            height: 28.785px !important;
                        `,
                    css`
                            width: 151.2405px !important;
                            height: 32.623px !important;
                        `,
                    css`
                            width: 169.0335px !important;
                            height: 36.461px !important;
                        `,
                    css`
                            width: 177.93px !important;
                            height: 38.38px !important;
                        `,
                ]);
            }}
            }
        `,
    },
    searchWrapper: {
        css: css`
            max-width: 457px      
        `,
    },
    navigation: {
        css: css`
            width: 100%;  
            display: flex;             
            > div {
                display: flex;
                justify-content: flex-end;
                gap: 35px;
                ${({ theme }) => {
                return breakpointMediaQueries(theme, [
                    css`
                            gap: 0;
                            button {
                                padding-right: 0;
                            }
                        `,
                    null,
                    null,
                    null,
                    null
                ]);
            }
            }

            > div:first-child {
                display: flex;
                flex-direction: row-reverse;
            }
        }
        `,
    },
    signIn: {
        css: css`
            display: flex;           
            width: 100%;
            padding: 10px;           
            color: ${getColor("primary")};
            &:last-child {
                justify-content: flex-end;
            }           

            button,
            span {
                justify-content: flex-end;
                color: ${getColor("primary")};
                font-weight: 700;
                font-size: 16px;
                text-transform: capitalize;
            }
        `,
    },
    container: {
        css: css`
            width: 100%;
            margin: 0 auto;                     
            ${({ theme }) => {
                const { maxWidths } = theme.breakpoints || baseTheme.breakpoints;
                return breakpointMediaQueries(theme, [
                    css`
                        max-width: ${maxWidths[1]}px;
                        padding: 0 15px; 
                    `,
                    css`
                        max-width: ${maxWidths[1]}px;
                    `,
                    css`
                        max-width: ${maxWidths[2]}px;
                    `,
                    css`
                        max-width: ${maxWidths[3]}px;
                    `,
                    css`
                        max-width: ${maxWidths[4]}px;
                    `,
                ]);
            }}
            padding-bottom: 0;
            padding-right: 5px;
            display: flex;
            align-items: center;
            height: 100%;                    
        `,
    },
    websiteName: {
        css: css`
            @media print {
                display: block;
            }
            font-size: 12px;
            padding-left: 15px;
            display: none;
        `,
    },
};

const styles = headerStyles;

const Header = ({ id, siteName, session, toaster, history, myAccountPageUrl, dismissRfqUpdates }: Props) => {
    const [scrollY, setScrollY] = useState<number>(0);

    function logit() {
        setScrollY(window.pageYOffset);
    }

    useEffect(() => {
        function watchScroll() {
            window.addEventListener("scroll", logit);
        }
        watchScroll();
        return () => {
            window.removeEventListener("scroll", logit);
        };

    },)

    const viewMyDashboardClickHandler = () => {
        if (myAccountPageUrl) {
            history.push(myAccountPageUrl);
        }
    };

    useEffect(() => {
        if (session.hasRfqUpdates) {
            toaster.addToast({
                body: (
                    <>
                        <Typography>{translate("You have new quote information.")}</Typography>
                        <Link onClick={viewMyDashboardClickHandler}>{translate("View My Dashboard")}</Link>
                    </>
                ),
                messageType: "info",
                timeoutLength: 1000 * 60 * 60 * 24 * 365,
                onClose: () => {
                    dismissRfqUpdates();
                },
            });
        }
    }, []);

    const afterHeader = React.createRef<HTMLSpanElement>();
    return (
        <>
            {siteName && <Typography {...styles.websiteName}>{siteName}</Typography>}
            <Page
                as="header"
                isHeader={true}
                {...styles.page}
                className={scrollY > 25 ? "hide-header" : ""}
            >
                <StyledWrapper {...styles.container}>
                    <StyledWrapper {...styles.logoWrapper}>
                        <Zone contentId={id} zoneName="Logo" />
                    </StyledWrapper>
                    <Hidden below="lg">
                        <StyledWrapper {...styles.searchWrapper}>
                            <Zone zoneName="HeaderSearchInput" contentId={id} />
                        </StyledWrapper>
                    </Hidden>
                    <StyledWrapper {...styles.navigation}>
                        <Zone contentId={id} zoneName="MainNavigation" />
                    </StyledWrapper>
                    <Hidden below="lg">
                        <StyledWrapper {...styles.signIn}>
                            <Zone zoneName="SignIn" contentId={id} />
                        </StyledWrapper>
                    </Hidden>
                </StyledWrapper>
                <span ref={afterHeader} tabIndex={-1} />
            </Page>
        </>
    );
};

const pageModule: PageModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(withToaster(withHistory(Header))),
    definition: {
        hasEditableTitle: false,
        hasEditableUrlSegment: false,
        pageType: "System",
    },
};

export const HeaderContext = "HeaderContext";

export default pageModule;
